import { useEffect, useMemo, useRef, useState } from "react";
import { usePlatformOrg } from "../../../utils/context/PlatformOrgContext";
import "./OrgSwitcher.css";

/**
 * Header autocomplete for the Platform super-admin portal: type-to-filter list of every organization. Selecting
 * one makes the whole portal act as that org (see PlatformOrgContext). Built as a small controlled combobox since
 * the app has no typeahead widget.
 */
const OrgSwitcher = () => {
  const { orgs, selectedOrgId, selectOrg } = usePlatformOrg();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOrg = orgs.find((o) => Number(o.id) === selectedOrgId);
  const selectedName = selectedOrg?.name ?? "";

  // Close on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name ?? "").toLowerCase().includes(q));
  }, [orgs, query]);

  if (!orgs || orgs.length === 0) return null;

  const handleSelect = (orgId: number) => {
    selectOrg(orgId);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="org-switcher" ref={containerRef}>
      <button
        type="button"
        className="org-switcher-toggle normal_text"
        onClick={() => setOpen((v) => !v)}
        title="Viewing organization"
      >
        <span className="org-switcher-label">{selectedName || "Select organization"}</span>
        <span className="org-switcher-caret">▾</span>
      </button>

      {open && (
        <div className="org-switcher-menu">
          <input
            className="org-switcher-input normal_text"
            type="text"
            autoFocus
            placeholder="Search organizations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul className="org-switcher-list">
            {filtered.length === 0 ? (
              <li className="org-switcher-empty sub_text">No organizations found</li>
            ) : (
              filtered.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className={
                      Number(o.id) === selectedOrgId
                        ? "org-switcher-item org-switcher-item-selected normal_text"
                        : "org-switcher-item normal_text"
                    }
                    onClick={() => handleSelect(Number(o.id))}
                  >
                    {o.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
